import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid, Step, StepButton, Stepper } from '@mui/material'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormHeader } from '../FormHeader'
import theme from '@utils/theme'
import { productOrderReceiptSchema } from '@utils/schemas'
import { CreateProductOrderReceiptDto, ProductOrderDto, ProductType } from '@services/dtos'
import { createProductOrderReceipt, resetSelectedNewProducts, resetSelectedStorageLocations } from '@state/reducers/productOrderReceiptReducer'
import ProductOrderReceiptStepProducts from './steps/ProductOrderReceiptStepProducts'
import ProductOrderReceiptStepInfo from './steps/ProductOrderReceiptStepInfo'
import { getProductOrders, resetProductOrderReceiptFiles } from '@state/reducers/productOrderReducer'
import { truncate } from 'lodash'
import { useSelector } from '@state/store'

export enum ProductOrderReceiptFormStep {
  Products,
  Info
}

const steps: Record<ProductOrderReceiptFormStep, string> = {
  [ProductOrderReceiptFormStep.Products]: 'Produits',
  [ProductOrderReceiptFormStep.Info]: 'Informations',
}

type Props = {
  productOrder: ProductOrderDto
  onClose: () => void
}

const ProductOrderReceiptForm: React.FC<Props> = ({ onClose, productOrder }) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [step, setStep] = useState<ProductOrderReceiptFormStep>(ProductOrderReceiptFormStep.Products)
  const { deliveryNote, releaseCertificate } = useSelector(({ productOrder }) => ({
    deliveryNote: productOrder.productOrderReceiptFiles.deliveryNote,
    releaseCertificate: productOrder.productOrderReceiptFiles.releaseCertificate,
  }))
  
  const useCreateForm = useForm<CreateProductOrderReceiptDto>({
    resolver: yupResolver(productOrderReceiptSchema(step)),
    mode: 'onSubmit',
    defaultValues: {
      useOrderSupplier: true, 
      markOrderAsReceived: false, 
      lineItems: [],
      newProducts: [],
      orderId: productOrder.id, 
      useConnectedMemberAsReceiver: true 
    },
  })

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      useCreateForm.reset()
      dispatch(resetProductOrderReceiptFiles())
      dispatch(resetSelectedNewProducts())
      dispatch(resetSelectedStorageLocations())
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateProductOrderReceiptDto) => {
    setIsSubmitting(true)
    const {
      useOrderSupplier,
      useConnectedMemberAsReceiver,
      lineItems,
      newProducts,
      ...rest
    } = data
    lineItems.forEach(item => {
      if (!item.activityAtRefMbq) {
        return
      }
      if (productOrder.lineItems.find(({ product }) => product.id === item.productId)?.product.type === ProductType.GENERATOR) {
        item.activityAtRefMbq = item.activityAtRefMbq * 1000 // Gbq => Mbq
      }
    })
    const formData = new FormData()
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })
    formData.append('lineItems', JSON.stringify(lineItems))
    formData.append('newProducts', JSON.stringify(newProducts))
    if (deliveryNote) {
      formData.append('deliveryNote', deliveryNote)
    }
    if (releaseCertificate) {
      formData.append('releaseCertificate', releaseCertificate)
    }
    dispatch(createProductOrderReceipt(formData as unknown as CreateProductOrderReceiptDto))
      .unwrap()
      .then(() => {
        cb('La réception a été sauvegardée avec succès')
        dispatch(getProductOrders())
        onClose()
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const nextStep = async () => {
    const valid = await useCreateForm.trigger()
    if (!valid) {
      return
    }
    if (step < ProductOrderReceiptFormStep.Info) {
      setStep(prev => prev + 1)
      return
    }
    useCreateForm.handleSubmit(onSubmitForm)()
  }

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          useCreateForm.handleSubmit(onSubmitForm)(e)
        }}
        className="w-full h-full flex flex-col"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Réception produits'}
          subTitles={[
            `Commande ${productOrder.internalOrderNumber}`,
            `${truncate(productOrder.supplier.name, { length: 25, omission: '…' })} 
            - ${new Date(productOrder.createdAt).toLocaleDateString('fr-FR')}`
          ]}
          onSubmit={nextStep}
          submitText={step < ProductOrderReceiptFormStep.Info ? 'Continuer' : 'Valider'}
          centerElement={
            <Stepper activeStep={step} alternativeLabel className="gap-3">
              {Object.entries(steps).map(([key, label]) => {
                const stepIndex = Number(key) as ProductOrderReceiptFormStep
                const isActive = stepIndex === step
                const isPast = stepIndex < step
                return (
                  <Step key={label}>
                    <StepButton
                      disabled={!isPast}
                      onClick={() => isPast && setStep(stepIndex)}
                      icon={null}
                    >
                      <span style={{ color: isActive ? '#3A0CA1' : theme.palette.primary.main }}>
                        {label}
                      </span>
                    </StepButton>
                  </Step>
                )
              })}
            </Stepper>
          }
        />

        <Grid
          container
          rowSpacing={2}
          columnSpacing={4}
          className="flex-1"
          sx={{ height: '100%' }}
        >
          {step === ProductOrderReceiptFormStep.Products && (
            <ProductOrderReceiptStepProducts useCreateForm={useCreateForm} productOrder={productOrder} />
          )}
          {step === ProductOrderReceiptFormStep.Info && (
            <ProductOrderReceiptStepInfo useCreateForm={useCreateForm} productOrder={productOrder} />
          )}
        </Grid>
      </form>
    </div>
  )
}

export default ProductOrderReceiptForm
